// src/components/social/SocialMap.jsx
// Carte sociale : approche declarative <Marker>/<Popup> (identique a MapSearch qui marche)
// pour que les clics sur les utilisateurs et les boutons d'invitation fonctionnent de facon fiable.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from '../../utils/leafletConfig';
import { useTheme } from '../../hooks/useTheme';
import * as socialApi from '../../utils/socialApi';

const KNOWN_CITIES = [
  { name: 'Douala', lat: 4.0511, lng: 9.7679 },
  { name: 'Yaoundé', lat: 3.8480, lng: 11.5021 },
  { name: 'Bafoussam', lat: 5.4737, lng: 10.4179 },
  { name: 'Bamenda', lat: 5.9597, lng: 10.1459 },
  { name: 'Garoua', lat: 9.3017, lng: 13.3921 },
  { name: 'Maroua', lat: 10.5956, lng: 14.3247 },
  { name: 'Ngaoundéré', lat: 7.3167, lng: 13.5833 },
  { name: 'Bertoua', lat: 4.5772, lng: 13.6846 },
  { name: 'Ebolowa', lat: 2.9167, lng: 11.1500 },
  { name: 'Kribi', lat: 2.9500, lng: 9.9000 },
  { name: 'Limbe', lat: 4.0225, lng: 9.2136 },
  { name: 'Buea', lat: 4.1560, lng: 9.2870 },
  { name: 'Dschang', lat: 5.4500, lng: 10.0500 },
  { name: 'Edéa', lat: 3.8000, lng: 10.1333 },
  { name: 'Kumba', lat: 4.6363, lng: 9.4470 },
  { name: 'Nkongsamba', lat: 4.9547, lng: 9.9401 },
  { name: 'Foumban', lat: 5.7263, lng: 10.9166 },
  { name: 'Bafang', lat: 5.1667, lng: 10.1833 },
  { name: 'Mbouda', lat: 5.6333, lng: 10.2500 },
  { name: 'Sangmélima', lat: 2.9333, lng: 11.9833 },
];

const GENDER_ACCENT = { homme: '#4299E1', femme: '#ED64A6', autre: '#9F7AEA' };
const GENDER_EMOJI = { homme: '👨', femme: '👩', autre: '🧑' };

function normalizeCityName(s) {
  return String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
function findCityByName(input) {
  const target = normalizeCityName(input);
  return KNOWN_CITIES.find((c) => normalizeCityName(c.name) === target) || null;
}
function extractErrorMessage(err) {
  if (!err) return null;
  return err?.response?.data?.error || err?.data?.error || err?.error || err?.message || null;
}
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function findNearestCity(lat, lng) {
  let best = KNOWN_CITIES[0];
  let bestDist = Infinity;
  KNOWN_CITIES.forEach((c) => {
    const d = haversineKm(lat, lng, c.lat, c.lng);
    if (d < bestDist) { bestDist = d; best = c; }
  });
  return best;
}
function jitterPosition(userId, lat, lng) {
  const seed = parseInt(userId, 10) || 0;
  const angleDeg = (seed * 137.508) % 360;
  const radius = 0.0012 + (seed % 7) * 0.0003;
  const rad = (angleDeg * Math.PI) / 180;
  return [lat + radius * Math.cos(rad), lng + radius * Math.sin(rad)];
}

function FitToUsers({ points, trigger, fallbackCenter }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) { map.flyTo(fallbackCenter, 12, { duration: 0.8 }); return; }
    if (points.length === 1) { map.flyTo(points[0], 14, { duration: 0.8 }); return; }
    const bounds = L.latLngBounds(points);
    map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 14, duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

// Construit l'icone d'un avatar utilisateur (ami = anneau dore + badge, autre = anneau gris)
function buildAvatarIcon(user, friend, theme) {
  const accent = GENDER_ACCENT[user.gender] || GENDER_ACCENT.autre;
  const emoji = GENDER_EMOJI[user.gender] || '🧑';
  const onlineDot = user.is_online
    ? `<span style="position:absolute;bottom:-1px;right:-1px;width:12px;height:12px;border-radius:50%;background:#48BB78;border:2px solid #FFFFFF;"></span>`
    : '';
  const friendBadge = friend
    ? `<span style="position:absolute;top:-3px;left:-3px;width:18px;height:18px;border-radius:50%;background:${theme.gold};display:flex;align-items:center;justify-content:center;border:2px solid #FFFFFF;font-size:10px;color:#FFF;">★</span>`
    : '';
  const inner = user.avatar
    ? `<img src="${user.avatar}" style="width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;" />`
    : `<div style="width:100%;height:100%;border-radius:50%;background:${accent};display:flex;align-items:center;justify-content:center;font-size:22px;">${emoji}</div>`;
  const ring = friend ? `linear-gradient(135deg, ${theme.gold}, #48BB78)` : `linear-gradient(135deg, #6B7A99, #3D4A66)`;

  return L.divIcon({
    className: 'social-avatar-marker',
    html: `<div style="position:relative;width:48px;height:48px;">
            <div style="width:48px;height:48px;border-radius:50%;padding:2px;background:${ring};box-shadow:0 4px 12px rgba(0,0,0,0.35);">
              <div style="width:100%;height:100%;border-radius:50%;overflow:hidden;border:2px solid ${theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A'};">${inner}</div>
            </div>
            ${friendBadge}${onlineDot}
          </div>`,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
}

const SocialMap = ({ city, userId, isAuthenticated, onRequireLogin, friendIds = new Set(), sentRequestIds = new Set(), onOpenChat, onInviteSent, onInviteCancelled }) => {
  const { theme } = useTheme();
  const [selectedCity, setSelectedCity] = useState(city || 'Douala');
  const [cityInput, setCityInput] = useState(city || 'Douala');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchingNearby, setSearchingNearby] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [fitTrigger, setFitTrigger] = useState(0);
  // Etat local des invitations envoyees (pour reaction immediate au clic, en plus des props)
  const [localSent, setLocalSent] = useState(new Set());
  const initialCityRef = useRef(city);
  const cityInputDebounce = useRef(null);

  const uid = (v) => String(v);

  useEffect(() => {
    if (city && city !== initialCityRef.current) {
      initialCityRef.current = city;
      setSelectedCity(city);
      setCityInput(city);
    }
  }, [city]);

  const loadUsers = useCallback(async (targetCity) => {
    if (!targetCity) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await socialApi.getMapUsers(targetCity, userId || 0);
      setUsers(res && res.users ? res.users : []);
      if (!res) setLoadError('Reponse vide du serveur.');
    } catch (err) {
      setUsers([]);
      setLoadError(extractErrorMessage(err) || 'Erreur de chargement inconnue.');
    } finally {
      setLoading(false);
      setFitTrigger((t) => t + 1);
    }
  }, [userId]);

  useEffect(() => {
    loadUsers(selectedCity);
    const interval = setInterval(() => loadUsers(selectedCity), 30000);
    return () => clearInterval(interval);
  }, [selectedCity, loadUsers]);

  const handleCityInputChange = (value) => {
    setCityInput(value);
    setStatusMessage(null);
    if (cityInputDebounce.current) clearTimeout(cityInputDebounce.current);
    cityInputDebounce.current = setTimeout(() => {
      const match = findCityByName(value);
      if (match) setSelectedCity(match.name);
      else if (value.trim().length >= 2) setStatusMessage({ text: `Ville "${value}" non reconnue.`, tone: 'warning' });
    }, 400);
  };

  const handleSearchNearby = () => {
    if (!navigator.geolocation) { setStatusMessage({ text: 'Geolocalisation indisponible.', tone: 'warning' }); return; }
    setSearchingNearby(true);
    setStatusMessage(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const nearest = findNearestCity(pos.coords.latitude, pos.coords.longitude);
        setSelectedCity(nearest.name);
        setCityInput(nearest.name);
        setSearchingNearby(false);
      },
      () => { setSearchingNearby(false); setStatusMessage({ text: 'Position indisponible.', tone: 'warning' }); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleInvite = useCallback(async (targetUserId) => {
    if (!isAuthenticated) { onRequireLogin(); return; }
    if (uid(targetUserId) === uid(userId)) return;
    // Reaction immediate : le bouton passe a "envoyee" tout de suite
    setLocalSent((prev) => new Set(prev).add(uid(targetUserId)));
    try {
      const res = await socialApi.sendFriendRequest(userId, targetUserId);
      if (onInviteSent) onInviteSent(uid(targetUserId), res?.requestId ?? res?.id ?? null);
    } catch (err) {
      // rollback si echec
      setLocalSent((prev) => { const n = new Set(prev); n.delete(uid(targetUserId)); return n; });
    }
  }, [isAuthenticated, onRequireLogin, userId, onInviteSent]);

  const handleMessageClick = useCallback((targetUserId, username) => {
    if (!isAuthenticated) { onRequireLogin(); return; }
    if (onOpenChat) onOpenChat(targetUserId, username);
  }, [isAuthenticated, onRequireLogin, onOpenChat]);

  const isFriend = useCallback((targetUserId) => friendIds.has(uid(targetUserId)), [friendIds]);
  const isInviteSent = useCallback((targetUserId) => sentRequestIds.has(uid(targetUserId)) || localSent.has(uid(targetUserId)), [sentRequestIds, localSent]);

  const fallbackCenter = (KNOWN_CITIES.find((c) => c.name === selectedCity) || KNOWN_CITIES[0]);
  const visibleUsers = users.filter((u) => uid(u.user_id) !== uid(userId));
  const onlineCount = visibleUsers.filter((u) => u.is_online).length;
  const usersWithJitter = visibleUsers.map((u) => {
    const [jLat, jLng] = jitterPosition(u.user_id, parseFloat(u.latitude), parseFloat(u.longitude));
    return { ...u, jLat, jLng };
  });
  const fitPoints = usersWithJitter.map((u) => [u.jLat, u.jLng]);

  const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '0' },
    toolbar: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' },
    cityInput: { flex: 1, minWidth: '140px', padding: '10px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '12px', color: theme.textPrimary, fontSize: '13px', outline: 'none' },
    nearbyBtn: { display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 16px', background: `linear-gradient(135deg, ${theme.gold}, #B8860B)`, border: 'none', borderRadius: '12px', color: '#FFFFFF', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' },
    countLine: { fontSize: '12px', color: theme.textSecondary, marginBottom: '10px' },
    countStrong: { color: theme.textPrimary, fontWeight: '700' },
    statusBanner: (tone) => ({ padding: '8px 12px', borderRadius: '9px', fontSize: '12px', textAlign: 'center', marginBottom: '10px', backgroundColor: tone === 'warning' ? 'rgba(237,137,54,0.12)' : 'rgba(255,215,0,0.10)', border: `1px solid ${tone === 'warning' ? 'rgba(237,137,54,0.35)' : 'rgba(255,215,0,0.3)'}`, color: tone === 'warning' ? '#DD6B20' : theme.gold }),
    errorBanner: { padding: '10px 12px', borderRadius: '9px', fontSize: '12px', textAlign: 'center', marginBottom: '10px', backgroundColor: 'rgba(245,101,101,0.10)', border: '1px solid rgba(245,101,101,0.35)', color: '#F56565' },
    mapFrame: { position: 'relative', borderRadius: '20px', padding: '3px', background: `linear-gradient(135deg, ${theme.gold}, rgba(184,134,11,0.3), ${theme.border})` },
    mapCard: { position: 'relative', height: '60vh', minHeight: '380px', borderRadius: '18px', overflow: 'hidden', backgroundColor: theme.bgCard },
    mapTopBar: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'linear-gradient(180deg, rgba(10,14,26,0.75), transparent)', pointerEvents: 'none' },
    mapTopBarText: { fontSize: '12px', fontWeight: '700', color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.5)' },
    mapTopBarOnline: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.5)' },
    onlineDotSmall: { width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#48BB78', flexShrink: 0 },
    legend: { position: 'absolute', bottom: '10px', left: '10px', zIndex: 399, display: 'flex', gap: '10px', padding: '7px 12px', borderRadius: '10px', backgroundColor: 'rgba(10,14,26,0.75)', pointerEvents: 'none' },
    legendItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: '600', color: '#FFFFFF' },
    legendDotFriend: { width: '9px', height: '9px', borderRadius: '50%', background: `linear-gradient(135deg, ${theme.gold}, #48BB78)` },
    legendDotOther: { width: '9px', height: '9px', borderRadius: '50%', background: 'linear-gradient(135deg, #6B7A99, #3D4A66)' },
    popupWrap: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' },
    popupName: { fontWeight: '700', fontSize: '14px' },
    popupLoc: { fontSize: '12px', color: '#666' },
    popupBtn: { marginTop: '6px', padding: '8px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', width: '100%' },
    listCard: { marginTop: '14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '16px', overflow: 'hidden' },
    listTitle: { fontSize: '13px', fontWeight: '700', color: theme.textPrimary, padding: '14px 14px 8px' },
    listScroll: { display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '320px', overflowY: 'auto', padding: '0 14px 14px' },
    listEmpty: { fontSize: '12px', color: theme.textSecondary, textAlign: 'center', padding: '12px' },
    listRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '10px' },
    listAvatarWrap: { position: 'relative', width: '38px', height: '38px', flexShrink: 0 },
    listAvatarImg: { width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' },
    listAvatarIconWrap: (accent) => ({ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }),
    listOnlineDot: { position: 'absolute', bottom: '-1px', right: '-1px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#48BB78', border: `2px solid ${theme.bgCard}` },
    listFriendDot: { position: 'absolute', top: '-2px', left: '-2px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: theme.gold, border: `2px solid ${theme.bgCard}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#FFF' },
    listInfo: { flex: 1, minWidth: 0 },
    listName: { fontSize: '13px', fontWeight: '600', color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    listLoc: { fontSize: '11px', color: theme.textSecondary },
    listInviteBtn: { padding: '7px 12px', fontSize: '11px', fontWeight: '700', borderRadius: '8px', border: 'none', backgroundColor: theme.gold, color: '#FFFFFF', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    listMessageBtn: { padding: '7px 12px', fontSize: '11px', fontWeight: '700', borderRadius: '8px', border: 'none', backgroundColor: '#48BB78', color: '#FFFFFF', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    listSentBadge: { padding: '7px 10px', fontSize: '11px', fontWeight: '700', color: '#48BB78', whiteSpace: 'nowrap', flexShrink: 0 },
  };

  return (
    <div style={styles.wrap}>
      <style>{`
        .social-avatar-marker { z-index: 600 !important; }
        .leaflet-marker-icon.social-avatar-marker { z-index: 600 !important; }
      `}</style>
      <div style={styles.toolbar}>
        <input style={styles.cityInput} type="text" list="social-known-cities" value={cityInput} onChange={(e) => handleCityInputChange(e.target.value)} placeholder="Rechercher une ville (Douala, Yaoundé...)" />
        <datalist id="social-known-cities">{KNOWN_CITIES.map((c) => <option key={c.name} value={c.name} />)}</datalist>
        <button style={styles.nearbyBtn} onClick={handleSearchNearby} disabled={searchingNearby}>🔍 {searchingNearby ? 'Recherche...' : 'A proximite'}</button>
      </div>

      <div style={styles.countLine}>
        {loading ? 'Chargement...' : (<><span style={styles.countStrong}>{visibleUsers.length}</span> personne{visibleUsers.length > 1 ? 's' : ''} a <span style={styles.countStrong}>{selectedCity}</span></>)}
      </div>

      {loadError && <div style={styles.errorBanner}>⚠️ {loadError}</div>}
      {statusMessage && <div style={styles.statusBanner(statusMessage.tone)}>{statusMessage.text}</div>}

      <div style={styles.mapFrame}>
        <div style={styles.mapCard}>
          <div style={styles.mapTopBar}>
            <span style={{ ...styles.mapTopBarText, pointerEvents: 'none' }}>✨ Fynho Social — {selectedCity}</span>
            <span style={{ ...styles.mapTopBarOnline, pointerEvents: 'none' }}><span style={styles.onlineDotSmall} /> {onlineCount} en ligne</span>
          </div>
          <MapContainer center={[fallbackCenter.lat, fallbackCenter.lng]} zoom={13} style={{ width: '100%', height: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitToUsers points={fitPoints} trigger={fitTrigger} fallbackCenter={[fallbackCenter.lat, fallbackCenter.lng]} />

            {usersWithJitter.map((user) => {
              const friend = isFriend(user.user_id);
              const invited = isInviteSent(user.user_id);
              return (
                <Marker
                  key={user.user_id}
                  position={[user.jLat, user.jLng]}
                  icon={buildAvatarIcon(user, friend, theme)}
                >
                  <Popup>
                    <div style={styles.popupWrap}>
                      <div style={styles.popupName}>
                        {user.username} {GENDER_EMOJI[user.gender] || ''} {friend ? '⭐' : ''}
                      </div>
                      <div style={styles.popupLoc}>📍 {user.district}</div>
                      <div style={styles.popupLoc}>{user.is_online ? '🟢 En ligne' : 'Hors ligne'} · {friend ? 'Ami' : 'Autre'}</div>
                      {friend ? (
                        <button
                          style={{ ...styles.popupBtn, backgroundColor: '#48BB78', color: '#FFFFFF' }}
                          onClick={() => handleMessageClick(user.user_id, user.username)}
                        >
                          💬 Envoyer un message
                        </button>
                      ) : invited ? (
                        <div style={{ ...styles.popupBtn, backgroundColor: 'rgba(72,187,120,0.15)', color: '#48BB78', textAlign: 'center' }}>
                          ✓ Invitation envoyee
                        </div>
                      ) : (
                        <button
                          style={{ ...styles.popupBtn, backgroundColor: theme.gold, color: '#FFFFFF' }}
                          onClick={() => handleInvite(user.user_id)}
                        >
                          {isAuthenticated ? 'Envoyer une invitation' : 'Se connecter pour inviter'}
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
          <div style={styles.legend}>
            <div style={styles.legendItem}><span style={styles.legendDotFriend} /> Ami</div>
            <div style={styles.legendItem}><span style={styles.legendDotOther} /> Autre</div>
          </div>
        </div>
      </div>

      <div style={styles.listCard}>
        <div style={styles.listTitle}>Personnes a {selectedCity}</div>
        <div style={styles.listScroll}>
          {loading && <div style={styles.listEmpty}>Chargement...</div>}
          {!loading && visibleUsers.length === 0 && <div style={styles.listEmpty}>Aucune personne trouvee ici pour le moment.</div>}
          {!loading && [...visibleUsers].sort((a, b) => (b.is_online ? 1 : 0) - (a.is_online ? 1 : 0)).map((user) => {
            const friend = isFriend(user.user_id);
            const invited = isInviteSent(user.user_id);
            const accent = GENDER_ACCENT[user.gender] || GENDER_ACCENT.autre;
            return (
              <div key={user.user_id} style={styles.listRow}>
                <div style={styles.listAvatarWrap}>
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.username} style={styles.listAvatarImg} />
                  ) : (
                    <div style={styles.listAvatarIconWrap(accent)}>{GENDER_EMOJI[user.gender] || '🧑'}</div>
                  )}
                  {user.is_online && <span style={styles.listOnlineDot} />}
                  {friend && <span style={styles.listFriendDot}>★</span>}
                </div>
                <div style={styles.listInfo}>
                  <div style={styles.listName}>{user.username}</div>
                  <div style={styles.listLoc}>{user.district} · {user.is_online ? '🟢 En ligne' : 'Hors ligne'} · {friend ? 'Ami' : 'Autre'}</div>
                </div>
                {friend ? (
                  <button style={styles.listMessageBtn} onClick={() => handleMessageClick(user.user_id, user.username)}>💬 Message</button>
                ) : invited ? (
                  <span style={styles.listSentBadge}>✓ Envoyee</span>
                ) : (
                  <button style={styles.listInviteBtn} onClick={() => handleInvite(user.user_id)}>{isAuthenticated ? 'Inviter' : '🔒 Se connecter'}</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SocialMap;